import { FC, useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

interface InfoIconButtonProps {
  label: string;
  info: string;
  /** Outer corner radius of the blurred border. */
  outerRadius?: string;
  /** Gap between the outer blur and the inner card (the blur border thickness). */
  padding?: string;
}

const InfoIconButton: FC<InfoIconButtonProps> = ({
  label,
  info,
  outerRadius = '12px',
  padding = '12px',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        aria-label={`More info about ${label}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ p: 0.25, color: 'info.main' }}
      >
        <InfoIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 300,
              mt: 1,
              position: 'relative',
              zIndex: 1,
              borderRadius: `calc(${outerRadius} - ${padding})`,
              backgroundColor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              overflow: 'visible',

              '&::before': {
                content: '""',
                position: 'absolute',
                top: `calc(-1 * ${padding})`,
                right: `calc(-1 * ${padding})`,
                bottom: `calc(-1 * ${padding})`,
                left: `calc(-1 * ${padding})`,
                borderRadius: outerRadius,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(.75px)',
                WebkitBackdropFilter: 'blur(.75px)',
                filter: 'blur(.75px)',
                zIndex: -1,
                pointerEvents: 'none',
              },
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            background: 'inherit',
            borderRadius: 'inherit',
            p: 1.5,
          }}
        >
          <Typography variant="body2">{info}</Typography>
        </Box>
      </Popover>
    </>
  );
};

export default InfoIconButton;
